import React, {useState} from "react"

import Validation, {Schema} from "@irati/utils/validation"

import {StyledInput, Chip} from "./styled"
import {SpacingProps} from "../global/css"
import Flex from "../Flex"
import {Body} from "../Typography"
import IconButton from "../Button/IconButton"
import {IconTypes} from "../Icon"
import theme from "../global/theme"
import Form from "../Form"

interface Props extends SpacingProps {
  name: string,
  placeholder: string,
  onChange: (values: string[]) => void,
  validation?: Schema<any>,
  items?: string[],
  alignChips?: "start" | "end",
  width?: number
}

const MultiChipsSelect: React.FC<Props> = ({name, placeholder, items, validation, alignChips = "end", width, onChange}) => {
  const [chips, setChips] = useState<string[]>(items || [])
  const [value, setValue] = useState<string | null>("")
  const [error, setError] = useState<string>("")

  const handleChange = (newValue: string) => {
    setValue(newValue)
    setError("")
  }

  const handleEnter = () => {
    setError("")
    onAdd()
  }

  const onAdd = () => {
    if (value) {
      Validation.validate()
        .then(() => {
          if (chips.indexOf(value) === -1) {
            chips.push(value)
            setValue("")
            setChips([...chips])
            onChange(chips)
          } else {
            setError("platform.defaultValidation.alreadyAdded")
          }
        })
        .catch(() => {})
    } else {
      setError("platform.defaultValidation.required")
    }
  }

  const onRemove = (item: string) => {
    const filtered = chips.filter((chip) => chip !== item)
    setChips(filtered)
    onChange(filtered)
  }

  const chipsToRender = alignChips === "end" ? chips : [...chips.reverse()]

  return (
    <Flex
      width={`${width || 100}%`}
      gap={theme.spacing.one}
      wrap="wrap"
      direction={alignChips === "end" ? "row" : "row-reverse"}
      justifyContent={alignChips === "end" ? "flex-start" : "flex-end"}>
      <Form>
        <StyledInput
          className={error ? "has-error" : ""}
          error={error}
          hideLabel
          style={{borderRight: "none"}}
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onEnter={handleEnter}
          validation={validation}
          addon={{
            after: (
              <IconButton inline icon={IconTypes.circlePlusFilled} onClick={onAdd} size={24} />
            )}} />
      </Form>
      {chipsToRender?.map((item: string) => <Chip key={item}>
        <Body color={theme.colors.filterChip}>{item}</Body>
        <IconButton marginLeft={1} inline icon={IconTypes.circleMinus} onClick={() => onRemove(item)} size={24} />
      </Chip>
      )}
    </Flex>
  )
}

export default MultiChipsSelect
